import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Edit, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import Header from '../components/Header';
import {
  ALL_DIMENSION_AXES,
  ALL_DIMENSION_UNITS,
  AXIS_SHORT,
  type DimensionAxis,
  type DimensionFeatureConfig,
  type DimensionUnit,
  formatDimensionConfigLabel,
  isValidDimensionConfig,
  parseDimensionConfig,
} from '../lib/productDimensions';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface AssetCategory {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
}

interface CategoryFeature {
  id: string;
  category_id: string;
  feature_name: string;
  feature_type: 'text' | 'number' | 'boolean' | 'select' | 'dimension';
  description: string | null;
  options: string[] | DimensionFeatureConfig | null;
  is_required: boolean;
  is_active: boolean;
  created_at: string;
}

const featureSchema = z.object({
  feature_name: z.string().min(1, 'Feature name is required').max(100),
  feature_type: z.enum(['text', 'number', 'boolean', 'select', 'dimension']),
  description: z.string().max(1000).optional(),
  options: z.array(z.string()).optional(),
  is_required: z.boolean().optional(),
  is_active: z.boolean().optional(),
});

type FeatureForm = z.infer<typeof featureSchema>;

export default function CategoryFeatures() {
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [category, setCategory] = useState<AssetCategory | null>(null);
  const [features, setFeatures] = useState<CategoryFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CategoryFeature | null>(null);
  const [optionsText, setOptionsText] = useState('');
  const [dimAxes, setDimAxes] = useState<DimensionAxis[]>(['width', 'depth', 'height']);
  const [dimUnit, setDimUnit] = useState<DimensionUnit>('cm');
  const [dimRequiredAxes, setDimRequiredAxes] = useState<DimensionAxis[]>(['width', 'depth']);
  const [saveError, setSaveError] = useState('');

  const form = useForm<FeatureForm>({
    resolver: zodResolver(featureSchema),
    defaultValues: {
      feature_name: '',
      feature_type: 'text',
      description: '',
      options: [],
      is_required: false,
      is_active: true,
    },
  });

  const selectedType = form.watch('feature_type');

  useEffect(() => {
    if (!categoryId) {
      navigate('/categories');
      return;
    }
    checkAuth();
    loadCategory();
    loadFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  // Set form values when editing changes
  useEffect(() => {
    if (editing && dialogOpen) {
      const formData = {
        feature_name: editing.feature_name,
        feature_type: editing.feature_type,
        description: editing.description || '',
        options: Array.isArray(editing.options) ? editing.options : [],
        is_required: editing.is_required,
        is_active: editing.is_active,
      };
            // Use setTimeout to ensure the form is rendered
      setTimeout(() => {
        form.reset(formData);
        form.setValue('feature_type', editing.feature_type);
      }, 100);

      setOptionsText(editing.options && Array.isArray(editing.options) ? editing.options.join('\n') : '');
      const dimConfig = parseDimensionConfig(editing.options);
      if (dimConfig) {
        setDimAxes(dimConfig.axes);
        setDimUnit(dimConfig.unit);
        setDimRequiredAxes(dimConfig.required_axes ?? dimConfig.axes);
      }
    }
  }, [editing, dialogOpen, form]);

  const resetDimensionConfig = () => {
    setDimAxes(['width', 'depth', 'height']);
    setDimUnit('cm');
    setDimRequiredAxes(['width', 'depth']);
  };

  const toggleDimAxis = (axis: DimensionAxis, checked: boolean) => {
    setDimAxes((prev) => {
      const next = checked ? [...prev, axis] : prev.filter((a) => a !== axis);
      return ALL_DIMENSION_AXES.filter((a) => next.includes(a));
    });
    if (!checked) setDimRequiredAxes((prev) => prev.filter((a) => a !== axis));
  };

  const toggleDimRequired = (axis: DimensionAxis, checked: boolean) => {
    setDimRequiredAxes((prev) => (checked ? [...prev, axis] : prev.filter((a) => a !== axis)));
  };

  const checkAuth = () => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (!token || !savedUser) {
      navigate('/login');
      return;
    }

    try {
      setUser(JSON.parse(savedUser));
    } catch (err) {
      console.error('Error parsing user data:', err);
      navigate('/login');
    }
  };

  const loadCategory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/asset-categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setCategory(data);
      } else {
        console.error('Failed to fetch category');
        navigate('/categories');
      }
    } catch (error) {
      console.error('Error loading category:', error);
      navigate('/categories');
    }
  };

  const loadFeatures = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/asset-categories/${categoryId}/features`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setFeatures(data);
      } else {
        console.error('Failed to fetch features');
      }
    } catch (error) {
      console.error('Error loading features:', error);
    } finally {
      setLoading(false);
    }
  };

  const startCreate = () => {
    setEditing(null);
    form.reset({
      feature_name: '',
      feature_type: 'text',
      description: '',
      options: [],
      is_required: false,
      is_active: true,
    });
    setOptionsText('');
    resetDimensionConfig();
    setSaveError('');
    setDialogOpen(true);
  };

  const startEdit = (feature: CategoryFeature) => {
    setEditing(feature);
    setDialogOpen(true);
  };

  const onSubmit = async (values: FeatureForm) => {
    setSubmitting(true);
    setSaveError('');
    try {
      const token = localStorage.getItem('token');

      let options: string[] | DimensionFeatureConfig | null = null;
      if (values.feature_type === 'select' && optionsText.trim()) {
        options = optionsText.split('\n').map((opt) => opt.trim()).filter(Boolean);
      } else if (values.feature_type === 'dimension') {
        if (!dimAxes.length) {
          setSaveError('Select at least one dimension axis');
          return;
        }
        options = {
          axes: dimAxes,
          unit: dimUnit,
          required_axes: dimRequiredAxes.filter((a) => dimAxes.includes(a)),
        };
      }

      const processedValues = { ...values, options };

      const url = editing
        ? `/api/asset-categories/features/${editing.id}`
        : `/api/asset-categories/${categoryId}/features`;
      const method = editing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(processedValues),
      });

      if (response.ok) {
        await loadFeatures();
        setDialogOpen(false);
        form.reset();
        setOptionsText('');
        resetDimensionConfig();
      } else {
        const err = await response.json().catch(() => ({}));
        setSaveError(err.error || err.message || 'Failed to save feature');
      }
    } catch (error) {
      console.error('Error saving feature:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteFeature = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this feature?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/asset-categories/features/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        await loadFeatures();
      } else {
        console.error('Failed to delete feature');
      }
    } catch (error) {
      console.error('Error deleting feature:', error);
    }
  };

  const canEdit = user?.roles?.some(role => ['admin', 'supervisor'].includes(role.key)) ?? false;
  const canDelete = user?.roles?.some(role => role.key === 'admin') ?? false;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="categories" />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center">Loading category features... Category ID: {categoryId}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="categories" />
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/categories')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Categories
          </Button>
        </div>

        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground">
              Features: {category?.name}
            </h1>
            <p className="mt-2 text-muted-foreground">
              Manage features for this category that assets can have
            </p>
          </div>
          {canEdit && (
            <Button onClick={startCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Add Feature
            </Button>
          )}
        </div>

        <div className="rounded-md border">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left font-semibold px-4 py-3">Feature Name</th>
                  <th className="text-left font-semibold px-4 py-3">Type</th>
                  <th className="text-left font-semibold px-4 py-3">Options</th>
                  <th className="text-left font-semibold px-4 py-3">Required</th>
                  <th className="text-left font-semibold px-4 py-3">Status</th>
                  <th className="text-right font-semibold px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {features.map((feature) => (
                  <tr key={feature.id} className="border-t">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{feature.feature_name}</div>
                      {feature.description && (
                        <div className="text-muted-foreground max-w-xs truncate">{feature.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="warning">{feature.feature_type}</Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {feature.feature_type === 'select' && Array.isArray(feature.options)
                        ? feature.options.join(', ')
                        : feature.feature_type === 'dimension' && isValidDimensionConfig(feature.options)
                          ? formatDimensionConfigLabel(feature.options)
                          : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={feature.is_required ? 'destructive' : 'secondary'}>
                        {feature.is_required ? 'Required' : 'Optional'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={feature.is_active ? 'default' : 'secondary'}>
                        {feature.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {(!canEdit && !canDelete) ? (
                        <span className="text-muted-foreground text-sm">No actions</span>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <span className="sr-only">Open menu</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canEdit && (
                              <DropdownMenuItem onClick={() => startEdit(feature)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Feature
                              </DropdownMenuItem>
                            )}
                            {canDelete && (
                              <DropdownMenuItem onClick={() => deleteFeature(feature.id)} className="text-red-600">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Feature
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <Dialog key={editing?.id || 'new'} open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Feature' : 'Create Feature'}</DialogTitle>
              <DialogDescription>
                {editing ? 'Update the feature details.' : 'Add a new feature for this category.'}
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form key={editing?.id || 'new'} onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="feature_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Feature Name</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., Material, Color, Size" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                                                                    <FormField
                  control={form.control}
                  name="feature_type"
                                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Type</FormLabel>
                        <select
                          {...field}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <option value="">Select type</option>
                          <option value="text">Text</option>
                          <option value="number">Number</option>
                          <option value="boolean">Boolean (Yes/No)</option>
                          <option value="select">Select (Dropdown)</option>
                          <option value="dimension">Dimensions (W×D×H)</option>
                        </select>
                        <FormMessage />
                      </FormItem>
                  )}
                />
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description (Optional)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Describe what this feature represents..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {selectedType === 'dimension' && (
                  <div className="space-y-4 rounded-md border p-4">
                    <div>
                      <label className="text-sm font-medium">Axes</label>
                      <div className="mt-2 flex flex-wrap gap-4">
                        {ALL_DIMENSION_AXES.map((axis) => (
                          <label key={axis} className="flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={dimAxes.includes(axis)}
                              onCheckedChange={(c) => toggleDimAxis(axis, !!c)}
                            />
                            {AXIS_SHORT[axis]} ({axis})
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Unit</label>
                      <select
                        value={dimUnit}
                        onChange={(e) => setDimUnit(e.target.value as DimensionUnit)}
                        className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        {ALL_DIMENSION_UNITS.map((u) => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Required axes</label>
                      <div className="mt-2 flex flex-wrap gap-4">
                        {dimAxes.map((axis) => (
                          <label key={axis} className="flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={dimRequiredAxes.includes(axis)}
                              onCheckedChange={(c) => toggleDimRequired(axis, !!c)}
                            />
                            {axis}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {saveError && <p className="text-sm text-red-600">{saveError}</p>}

                {selectedType === 'select' && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Options (one per line)</label>
                    <Textarea
                      placeholder="Fabric&#10;Leather&#10;Velvet&#10;Microfiber"
                      value={optionsText}
                      onChange={(e) => setOptionsText(e.target.value)}
                      className="mt-1"
                      rows={4}
                    />
                  </div>
                )}

                <div className="flex items-center space-x-6">
                  <FormField
                    control={form.control}
                    name="is_required"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Required field</FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="is_active"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Active</FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="secondary" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving...' : editing ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
